// Opérations immuables sur un rapport : { meta, sections: [{ id, title, level, annex, blocks: [...] }] }.
import { newNodeId } from "../../utils/offerCalc";

export const REPORT_BLOCKS = [
  { type: "text", label: "Texte" },
  { type: "figures", label: "Chiffres clés" },
  { type: "image", label: "Figure (image)" },
  { type: "table", label: "Tableau" },
  { type: "twin", label: "Jumeau 3D (page HTML)" },
];

export const newBlock = (type) => {
  const id = newNodeId("b");
  switch (type) {
    case "figures":
      return { id, type, items: [{ value: "…", label: "…", detail: "" }, { value: "…", label: "…", detail: "" }] };
    case "image":
      return { id, type, image: null, caption: "", width: 100 };
    case "table":
      return { id, type, caption: "", rows: [["Colonne 1", "Colonne 2"], ["", ""]] };
    case "twin":
      return { id, type, embed: null, poster: null, caption: "", height: 320 };
    default:
      return { id, type: "text", label: "", text: "", style: "plain" };
  }
};

export const newSection = (level = 1, annex = false) => ({
  id: newNodeId("s"), title: "", level, annex, page_break: false, hidden: false, blocks: [newBlock("text")],
});

const mapSection = (content, sectionId, fn) => ({
  ...content, sections: content.sections.map((section) => (section.id === sectionId ? fn(section) : section)),
});

const move = (list, index, delta) => {
  const target = index + delta;
  if (index < 0 || target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

const apply = (value, patch) => (typeof patch === "function" ? patch(value) : { ...value, ...patch });

export const updateMeta = (content, patch) => ({ ...content, meta: apply(content.meta, patch) });
export const updateSection = (content, sectionId, patch) => mapSection(content, sectionId, (s) => apply(s, patch));

export const insertSection = (content, afterId, section) => {
  const sections = [...content.sections];
  const index = sections.findIndex((item) => item.id === afterId);
  sections.splice(index < 0 ? sections.length : index + 1, 0, section);
  return { ...content, sections };
};
export const moveSection = (content, sectionId, delta) => ({
  ...content, sections: move(content.sections, content.sections.findIndex((s) => s.id === sectionId), delta),
});
export const removeSection = (content, sectionId) => ({
  ...content, sections: content.sections.filter((section) => section.id !== sectionId),
});

export const updateBlock = (content, sectionId, blockId, patch) => mapSection(content, sectionId, (section) => ({
  ...section, blocks: section.blocks.map((block) => (block.id === blockId ? apply(block, patch) : block)),
}));
export const addBlock = (content, sectionId, block, afterId) => mapSection(content, sectionId, (section) => {
  const blocks = [...(section.blocks || [])];
  const index = blocks.findIndex((item) => item.id === afterId);
  blocks.splice(index < 0 ? blocks.length : index + 1, 0, block);
  return { ...section, blocks };
});
export const moveBlock = (content, sectionId, blockId, delta) => mapSection(content, sectionId, (section) => ({
  ...section, blocks: move(section.blocks, section.blocks.findIndex((block) => block.id === blockId), delta),
}));
export const removeBlock = (content, sectionId, blockId) => mapSection(content, sectionId, (section) => ({
  ...section, blocks: section.blocks.filter((block) => block.id !== blockId),
}));
export const duplicateBlock = (content, sectionId, blockId) => {
  const section = content.sections.find((item) => item.id === sectionId);
  const block = section?.blocks.find((item) => item.id === blockId);
  return block ? addBlock(content, sectionId, { ...block, id: newNodeId("b") }, blockId) : content;
};

// Numéros des sections visibles : 1, 1.1, 2… puis annexes A, A.1, B…
export const sectionNumbers = (sections) => {
  const numbers = {};
  let main = 0;
  let sub = 0;
  let annex = 0;
  let annexSub = 0;
  (sections || []).forEach((section) => {
    if (section.hidden) return;
    if (section.annex) {
      if (section.level === 2 && annex) numbers[section.id] = `${String.fromCharCode(64 + annex)}.${++annexSub}`;
      else { annex += 1; annexSub = 0; numbers[section.id] = String.fromCharCode(64 + annex); }
    } else if (section.level === 2 && main) {
      numbers[section.id] = `${main}.${++sub}`;
    } else {
      main += 1;
      sub = 0;
      numbers[section.id] = String(main);
    }
  });
  return numbers;
};

// Numéros des figures (image, jumeau) et des tableaux, dans l'ordre du document.
export const captionNumbers = (sections) => {
  const numbers = {};
  let figure = 0;
  let table = 0;
  (sections || []).forEach((section) => {
    if (section.hidden) return;
    (section.blocks || []).forEach((block) => {
      if (block.type === "table") numbers[block.id] = `Tableau ${++table}`;
      else if (block.type === "image" || block.type === "twin") numbers[block.id] = `Figure ${++figure}`;
    });
  });
  return numbers;
};
